import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Markdown for anything a model wrote (日本-325).
 *
 * **Why this exists at all:** a model asked to explain Japanese grammar reaches for a table, and a
 * model asked to compare two readings reaches for a list. Rendered as plain text those arrive as
 * `| ─── | ─── |` rubble and `**emphasis**` with the asterisks showing — which reads as the model
 * having formatted badly rather than as the app having declined to render.
 *
 * `remark-gfm` for the half that matters here: **tables**, task lists, strikethrough and autolinks.
 * Vanilla CommonMark has no tables at all.
 *
 * **Raw HTML and images are refused.** `skipHtml` plus `disallowedElements` — a model has no reason
 * to emit either, and not rendering them means there is no sanitisation surface to get wrong. This
 * is the one place in the app that renders text a model produced, so the narrow surface is the
 * point rather than an economy.
 *
 * Every override below maps to `tokens.css` rather than inlining a colour, per the repo rule, and
 * the type scale is the dock's — a `#` heading in a 700px column must not render at 32px.
 *
 * **A table scrolls inside its own box.** The app forbids horizontal scrolling of the page at any
 * width; a wide table is exactly the content that would otherwise cause it, so the `overflow-x`
 * lives on a wrapper and the page never moves.
 */
export function Markdown({ children }: { children: string }): ReactNode {
  if (!children) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      skipHtml
      disallowedElements={["img", "script", "style", "iframe"]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        // Headings collapse to bold text. A model writing `## Grammar` means "here is a section",
        // not "start a document" — and the dock has one type scale, which the surrounding
        // transcript already sets.
        h1: ({ children }) => <p className="mb-1 mt-2 font-semibold text-ink">{children}</p>,
        h2: ({ children }) => <p className="mb-1 mt-2 font-semibold text-ink">{children}</p>,
        h3: ({ children }) => <p className="mb-1 mt-2 font-semibold text-ink">{children}</p>,
        h4: ({ children }) => <p className="mb-1 mt-1 font-semibold text-ink">{children}</p>,
        h5: ({ children }) => <p className="mb-1 mt-1 font-semibold text-ink">{children}</p>,
        h6: ({ children }) => <p className="mb-1 mt-1 font-semibold text-ink">{children}</p>,
        ul: ({ children }) => (
          <ul className="mb-2 list-disc space-y-[2px] pl-[18px] last:mb-0">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="mb-2 list-decimal space-y-[2px] pl-[18px] last:mb-0">{children}</ol>
        ),
        li: ({ children }) => <li className="marker:text-ink-4">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        del: ({ children }) => <del className="line-through opacity-70">{children}</del>,
        code: ({ children, className }) => {
          // `language-…` marks a fenced block; anything else is inline. `react-markdown` routes
          // both through this one component, so the branch is how they are told apart.
          const block = typeof className === "string" && className.startsWith("language-");
          if (block) {
            return (
              <code className="my-2 block overflow-x-auto whitespace-pre rounded-[var(--r-2)] border border-line bg-sunken p-2 font-mono text-[14px]/[1.45] text-ink-2">
                {children}
              </code>
            );
          }
          return (
            <code className="rounded-[var(--r-2)] bg-sunken px-1 py-[1px] font-mono text-[11.5px] text-ink-2">
              {children}
            </code>
          );
        },
        // `pre` is dropped: the `code` above already draws the box, and keeping both nests one
        // bordered block inside another.
        pre: ({ children }) => <>{children}</>,
        blockquote: ({ children }) => (
          <blockquote className="mb-2 border-l-2 border-line-strong pl-[10px] italic text-ink-3 last:mb-0">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-3 border-line-soft" />,
        a: ({ children, href }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-text underline underline-offset-2"
          >
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="my-2 overflow-x-auto rounded-[var(--r-2)] border border-line">
            <table className="w-full border-collapse text-[11.5px]/[1.4]">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-sunken">{children}</thead>,
        tr: ({ children }) => (
          <tr className="border-b border-line-soft last:border-b-0">{children}</tr>
        ),
        th: ({ children, style }) => (
          <th
            style={style}
            className="border-r border-line-soft px-2 py-1 text-left font-semibold text-ink last:border-r-0"
          >
            {children}
          </th>
        ),
        td: ({ children, style }) => (
          <td
            style={style}
            className="border-r border-line-soft px-2 py-1 align-top last:border-r-0"
          >
            {children}
          </td>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}
